/**
 * Tabbed editor for lesson prompts
 * Allows editing AI prompt, chat intro, narrative, and other fields
 */

import { useState, useEffect, useCallback } from 'react';
import { useLessonPrompt } from '@/hooks/useLessonPrompts';
import { LessonPromptInput, ContextScenario, QuickPrompt } from '@/types/lessonPrompt';
import { microSessionService } from '@/services/microSessionService';
import { getChatIntro } from '@/data/microSessionChatIntros';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, Trash2, RotateCcw, Loader2, AlertCircle, FileCode } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PromptEditorProps {
  lessonId: string;
  onSave?: () => void;
}

export function PromptEditor({ lessonId, onSave }: PromptEditorProps) {
  const { prompt, loading, saving, error, savePrompt, deletePrompt } = useLessonPrompt(lessonId);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Get file-based defaults for comparison and fallback
  const fileSession = microSessionService.getSessionById(lessonId);
  const fileChatIntro = getChatIntro(lessonId);

  // Form state
  const [formData, setFormData] = useState<LessonPromptInput>({
    lessonId,
    aiPrompt: '',
    chatIntro: '',
    introPrompt: '',
    promptGuidance: '',
    narrativeProblem: '',
    narrativeWhat: '',
    narrativeWhy: '',
    narrativeHow: '',
    narrativeBridge: '',
    contextScenarios: [],
    quickPrompts: [],
  });

  // Initialize form with DB or file data
  useEffect(() => {
    if (prompt) {
      // Use DB values
      setFormData({
        lessonId,
        aiPrompt: prompt.aiPrompt || '',
        chatIntro: prompt.chatIntro || '',
        introPrompt: prompt.introPrompt || '',
        promptGuidance: prompt.promptGuidance || '',
        narrativeProblem: prompt.narrative?.problem || '',
        narrativeWhat: prompt.narrative?.what || '',
        narrativeWhy: prompt.narrative?.why || '',
        narrativeHow: prompt.narrative?.how || '',
        narrativeBridge: prompt.narrativeBridge || '',
        contextScenarios: prompt.contextScenarios || [],
        quickPrompts: prompt.quickPrompts || [],
      });
    } else if (fileSession) {
      // Use file defaults
      setFormData({
        lessonId,
        aiPrompt: fileSession.aiPrompt || '',
        chatIntro: fileChatIntro || '',
        introPrompt: fileSession.introPrompt || '',
        promptGuidance: fileSession.promptGuidance || '',
        narrativeProblem: fileSession.narrative?.problem || '',
        narrativeWhat: fileSession.narrative?.what || '',
        narrativeWhy: fileSession.narrative?.why || '',
        narrativeHow: fileSession.narrative?.how || '',
        narrativeBridge: '',
        contextScenarios: fileSession.contextScenarios || [],
        quickPrompts: fileSession.quickPrompts || [],
      });
    }
    setHasChanges(false);
  }, [prompt, fileSession, fileChatIntro, lessonId]);

  const updateField = useCallback(
    <K extends keyof LessonPromptInput>(field: K, value: LessonPromptInput[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setHasChanges(true);
    },
    []
  );

  const handleSave = async () => {
    const result = await savePrompt(formData);
    if (result) {
      setHasChanges(false);
      onSave?.();
    }
  };

  const handleDelete = async () => {
    await deletePrompt();
    setShowDeleteDialog(false);
    onSave?.();
  };

  const handleRevertToFile = () => {
    if (fileSession) {
      setFormData({
        lessonId,
        aiPrompt: fileSession.aiPrompt || '',
        chatIntro: fileChatIntro || '',
        introPrompt: fileSession.introPrompt || '',
        promptGuidance: fileSession.promptGuidance || '',
        narrativeProblem: fileSession.narrative?.problem || '',
        narrativeWhat: fileSession.narrative?.what || '',
        narrativeWhy: fileSession.narrative?.why || '',
        narrativeHow: fileSession.narrative?.how || '',
        narrativeBridge: '',
        contextScenarios: fileSession.contextScenarios || [],
        quickPrompts: fileSession.quickPrompts || [],
      });
      setHasChanges(true);
    }
  };

  // JSON editor for context scenarios
  const [scenariosJson, setScenariosJson] = useState('');
  const [scenariosError, setScenariosError] = useState<string | null>(null);

  useEffect(() => {
    setScenariosJson(JSON.stringify(formData.contextScenarios, null, 2));
  }, [formData.contextScenarios]);

  const handleScenariosChange = (json: string) => {
    setScenariosJson(json);
    try {
      const parsed = JSON.parse(json) as ContextScenario[];
      updateField('contextScenarios', parsed);
      setScenariosError(null);
    } catch {
      setScenariosError('Invalid JSON');
    }
  };

  // JSON editor for quick prompts
  const [quickPromptsJson, setQuickPromptsJson] = useState('');
  const [quickPromptsError, setQuickPromptsError] = useState<string | null>(null);

  useEffect(() => {
    setQuickPromptsJson(JSON.stringify(formData.quickPrompts, null, 2));
  }, [formData.quickPrompts]);

  const handleQuickPromptsChange = (json: string) => {
    setQuickPromptsJson(json);
    try {
      const parsed = JSON.parse(json) as QuickPrompt[];
      updateField('quickPrompts', parsed);
      setQuickPromptsError(null);
    } catch {
      setQuickPromptsError('Invalid JSON');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error.message}
        </div>
      )}

      {/* Source indicator */}
      <div className="mb-4 flex items-center gap-2 text-sm">
        {prompt ? (
          <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
            Using database values (v{prompt.version})
          </span>
        ) : (
          <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full flex items-center gap-1">
            <FileCode className="w-4 h-4" />
            Using file defaults (no DB override yet)
          </span>
        )}
      </div>

      <Tabs defaultValue="ai-prompt" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="ai-prompt">AI Prompt</TabsTrigger>
          <TabsTrigger value="chat-intro">Chat Intro</TabsTrigger>
          <TabsTrigger value="narrative">Narrative</TabsTrigger>
          <TabsTrigger value="scenarios">Scenarios</TabsTrigger>
          <TabsTrigger value="quick-prompts">Quick Prompts</TabsTrigger>
        </TabsList>

        {/* AI Prompt Tab */}
        <TabsContent value="ai-prompt" className="space-y-4">
          <div>
            <Label htmlFor="aiPrompt">System Prompt</Label>
            <p className="text-sm text-gray-500 mb-2">
              The main instruction sent to the AI that defines how Lyra behaves for this lesson.
            </p>
            <Textarea
              id="aiPrompt"
              value={formData.aiPrompt}
              onChange={(e) => updateField('aiPrompt', e.target.value)}
              className="font-mono text-sm min-h-[400px]"
              placeholder="Enter the system prompt..."
            />
            <p className="text-xs text-gray-400 mt-1">
              {formData.aiPrompt?.length || 0} characters
            </p>
          </div>

          <div>
            <Label htmlFor="introPrompt">Intro Prompt (Optional)</Label>
            <p className="text-sm text-gray-500 mb-2">
              Additional context prepended to user messages.
            </p>
            <Textarea
              id="introPrompt"
              value={formData.introPrompt}
              onChange={(e) => updateField('introPrompt', e.target.value)}
              className="font-mono text-sm min-h-[100px]"
              placeholder="Optional intro prompt..."
            />
          </div>

          <div>
            <Label htmlFor="promptGuidance">Prompt Guidance (Optional)</Label>
            <p className="text-sm text-gray-500 mb-2">
              Guidance shown to users about how to write their prompt.
            </p>
            <Textarea
              id="promptGuidance"
              value={formData.promptGuidance}
              onChange={(e) => updateField('promptGuidance', e.target.value)}
              className="text-sm min-h-[100px]"
              placeholder="Tips for users on how to approach this lesson..."
            />
          </div>
        </TabsContent>

        {/* Chat Intro Tab */}
        <TabsContent value="chat-intro" className="space-y-4">
          <div>
            <Label htmlFor="chatIntro">Opening Message</Label>
            <p className="text-sm text-gray-500 mb-2">
              The first message users see when they start the lesson. This appears before any
              AI interaction.
            </p>
            <Textarea
              id="chatIntro"
              value={formData.chatIntro}
              onChange={(e) => updateField('chatIntro', e.target.value)}
              className="text-sm min-h-[300px]"
              placeholder="Enter the opening message..."
            />
            <p className="text-xs text-gray-400 mt-1">
              {formData.chatIntro?.length || 0} characters
            </p>
          </div>
        </TabsContent>

        {/* Narrative Tab */}
        <TabsContent value="narrative" className="space-y-4">
          <p className="text-sm text-gray-500 mb-4">
            The narrative bridge that appears on the lesson card before users start.
          </p>

          <div>
            <Label htmlFor="narrativeProblem">Problem</Label>
            <Textarea
              id="narrativeProblem"
              value={formData.narrativeProblem}
              onChange={(e) => updateField('narrativeProblem', e.target.value)}
              className="text-sm"
              placeholder="What problem does this lesson address?"
            />
          </div>

          <div>
            <Label htmlFor="narrativeWhat">What</Label>
            <Textarea
              id="narrativeWhat"
              value={formData.narrativeWhat}
              onChange={(e) => updateField('narrativeWhat', e.target.value)}
              className="text-sm"
              placeholder="What will the user learn or practice?"
            />
          </div>

          <div>
            <Label htmlFor="narrativeWhy">Why</Label>
            <Textarea
              id="narrativeWhy"
              value={formData.narrativeWhy}
              onChange={(e) => updateField('narrativeWhy', e.target.value)}
              className="text-sm"
              placeholder="Why does this matter?"
            />
          </div>

          <div>
            <Label htmlFor="narrativeHow">How</Label>
            <Textarea
              id="narrativeHow"
              value={formData.narrativeHow}
              onChange={(e) => updateField('narrativeHow', e.target.value)}
              className="text-sm"
              placeholder="How will the user accomplish this?"
            />
          </div>

          <div>
            <Label htmlFor="narrativeBridge">Narrative Bridge (Optional)</Label>
            <p className="text-sm text-gray-500 mb-2">
              A story-driven intro paragraph shown on the lesson card.
            </p>
            <Textarea
              id="narrativeBridge"
              value={formData.narrativeBridge}
              onChange={(e) => updateField('narrativeBridge', e.target.value)}
              className="text-sm min-h-[100px]"
              placeholder="Once upon a time at your nonprofit..."
            />
          </div>
        </TabsContent>

        {/* Context Scenarios Tab */}
        <TabsContent value="scenarios" className="space-y-4">
          <div>
            <Label>Context Scenarios (JSON)</Label>
            <p className="text-sm text-gray-500 mb-2">
              Array of scenario options users can choose from. Each scenario has title, description,
              and message.
            </p>
            <Textarea
              value={scenariosJson}
              onChange={(e) => handleScenariosChange(e.target.value)}
              className="font-mono text-sm min-h-[300px]"
              placeholder='[{"title": "Scenario 1", "description": "...", "message": "..."}]'
            />
            {scenariosError && (
              <p className="text-red-500 text-sm mt-1">{scenariosError}</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {formData.contextScenarios?.length || 0} scenarios
            </p>
          </div>
        </TabsContent>

        {/* Quick Prompts Tab */}
        <TabsContent value="quick-prompts" className="space-y-4">
          <div>
            <Label>Quick Prompts (JSON)</Label>
            <p className="text-sm text-gray-500 mb-2">
              Array of quick action buttons. Can be strings or objects with label and message.
            </p>
            <Textarea
              value={quickPromptsJson}
              onChange={(e) => handleQuickPromptsChange(e.target.value)}
              className="font-mono text-sm min-h-[300px]"
              placeholder='["Simple prompt", {"label": "Button Label", "message": "Full message"}]'
            />
            {quickPromptsError && (
              <p className="text-red-500 text-sm mt-1">{quickPromptsError}</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {formData.quickPrompts?.length || 0} prompts
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Action Buttons */}
      <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRevertToFile} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-1" />
            Revert to File Defaults
          </Button>
          {prompt && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              disabled={saving}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete DB Override
            </Button>
          )}
        </div>

        <div className="flex items-center gap-4">
          {hasChanges && <span className="text-sm text-amber-600">Unsaved changes</span>}
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-1" />
                Save to Database
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Database Override?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the database prompt for "{lessonId}" and revert to file-based
              defaults. This action cannot be undone (though you can restore from history).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
